import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { getCustomers } from 'src/selectors/customer'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

interface Props {
  value: string | null
  onChange(partnerId: string | null): void
  label?: string
  placeholder?: string
  dataTestId?: string
}

const ALL_PARTNERS_VALUE = ''

/** Searchable partner filter with an "All partners" option that clears it. */
const PartnerFilterSelectComponent = ({
  value,
  onChange,
  label = 'Partner',
  placeholder = 'All partners',
  dataTestId,
}: Props) => {
  const customers = useSelector(getCustomers)

  const options = useMemo(() => {
    const partnerOptions = customers.map((customer) => {
      return { value: customer.id, label: resolveCustomerDisplayName(customer) }
    })

    return [{ value: ALL_PARTNERS_VALUE, label: 'All partners' }, ...partnerOptions]
  }, [customers])

  const handleChange = useCallback(
    (nextValue: string | number) => {
      const partnerId = String(nextValue).trim()

      onChange(partnerId === ALL_PARTNERS_VALUE ? null : partnerId)
    },
    [onChange],
  )

  return (
    <div data-testid={dataTestId} css={tw`flex w-[220px] flex-col`}>
      <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
        {label}
      </Typography>
      <ProtoSelectStandalone
        value={value ?? ALL_PARTNERS_VALUE}
        options={options}
        placeholder={placeholder}
        searchable
        searchPlaceholder="Search partners…"
        onValueChange={handleChange}
      />
    </div>
  )
}

export const PartnerFilterSelect = memo(PartnerFilterSelectComponent)
